import { BadgeCheck, Briefcase, FileCheck, Star, Shield, CheckCircle } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const verifications = [
  {
    icon: BadgeCheck,
    title: "Identité vérifiée",
    description: "Chaque transporteur fournit une pièce d'identité valide. Nous vérifions les documents.",
  },
  {
    icon: Briefcase,
    title: "Activité professionnelle",
    description: "Numéro de SIRET, licence de transport, attestation d'assurance. Processus structuré.",
  },
  {
    icon: FileCheck,
    title: "Responsabilité douane",
    description: "Nos transporteurs connaissent les procédures douanières France-Maroc.",
  },
  {
    icon: Star,
    title: "Avis clients",
    description: "Après chaque transport, les clients peuvent noter le service. Transparence totale.",
  },
];

const VerifiedCarriersSection = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="transporteurs" className="py-12 md:py-20 bg-primary text-primary-foreground">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Visual */}
          <div ref={leftRef} className={`order-2 lg:order-1 transition-all duration-700 ${leftVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="relative max-w-sm mx-auto">
              {/* Main badge */}
              <div className="bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 rounded-2xl p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                  <Shield className="w-8 h-8 text-secondary-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Transporteur Vérifié</h3>
                <p className="text-primary-foreground/80 mb-4 text-sm">GoBledPro</p>
                
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="bg-secondary/30 text-secondary-foreground font-bold px-3 py-1.5 rounded-full text-sm border border-secondary/50 flex items-center gap-1.5">
                    Identité <CheckCircle className="w-4 h-4 text-secondary" strokeWidth={3} />
                  </span>
                  <span className="bg-secondary/30 text-secondary-foreground font-bold px-3 py-1.5 rounded-full text-sm border border-secondary/50 flex items-center gap-1.5">
                    Documents <CheckCircle className="w-4 h-4 text-secondary" strokeWidth={3} />
                  </span>
                  <span className="bg-secondary/30 text-secondary-foreground font-bold px-3 py-1.5 rounded-full text-sm border border-secondary/50 flex items-center gap-1.5">
                    Profil validé <CheckCircle className="w-4 h-4 text-secondary" strokeWidth={3} />
                  </span>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className={`absolute -top-3 -left-3 bg-secondary text-secondary-foreground font-bold rounded-xl px-3 py-2 shadow-xl text-sm animate-float transition-all duration-700 flex items-center gap-1.5 ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
                <CheckCircle className="w-4 h-4" strokeWidth={3} /> Vérification structurée
              </div>
              <div className={`absolute -bottom-3 -right-3 bg-primary-foreground text-primary font-bold rounded-xl px-3 py-2 shadow-xl text-sm animate-float transition-all duration-700 flex items-center gap-1.5 ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ animationDelay: "1s", transitionDelay: '500ms' }}>
                <CheckCircle className="w-4 h-4" strokeWidth={3} /> Mise en relation
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div ref={rightRef} className={`order-1 lg:order-2 transition-all duration-700 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <span className="inline-block bg-secondary/20 text-secondary px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Vérification structurée
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-4">
              Des transporteurs{" "}
              <span className="text-secondary">rigoureusement vérifiés</span>
            </h2>
            <p className="text-base md:text-lg text-primary-foreground/90 mb-6">
              Nous mettons en place un processus de vérification structuré pour les transporteurs de notre réseau. Votre tranquillité d'esprit est notre priorité.
            </p>

            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 bg-destructive/20 border border-destructive/30 text-primary-foreground px-3 py-2 rounded-lg mb-6 text-sm">
              <Shield className="w-4 h-4 text-destructive" />
              <span className="font-medium">Processus de vérification obligatoire pour tous les transporteurs.</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {verifications.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-start gap-3 bg-primary-foreground/10 border border-primary-foreground/20 rounded-xl p-3 transition-all duration-500 ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <div className="w-11 h-11 rounded-lg bg-secondary/40 border border-secondary/60 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-secondary" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold mb-0.5 text-primary-foreground text-base">{item.title}</h3>
                    <p className="text-sm text-primary-foreground/90">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerifiedCarriersSection;
